import { ReactNode, useEffect, useMemo } from 'react';
import { UserProvider } from '../../api/user.provider';
import { IntercomSupportContextProvider } from '../../components/support/support.context';
import { ApplicationConfig } from '../../config/config';
import { useIsKioskModeEnabled } from '../../kiosk/kiosk';
import { DirectConnectApiProvider } from '../api/direct-connect-api.provider';
import {
  DirectConnectNetworkContext,
  DirectConnectNetworkContextValue,
  useDirectConnectNetworkContext,
} from '../api/direct-connect-network.provider';
import {
  getBeaconWifiSsid,
  getDirectConnectClientUrl,
} from '../helper/direct-connect-helper';
import { DirectConnectDeactivateGate } from './direct-connect-deactivate-gate';
import {
  DirectConnectGateKeeper,
  useDirectConnectGateKeeper,
} from './direct-connect-gate-keeper';
import {
  DirectConnectHealthGate,
  useDirectConnectHealthGateContext,
} from './direct-connect-health-gate';
import { DirectConnectProvisioningGate } from './direct-connect-provisioning-gate';
import { DirectConnectRegistrationGate } from './direct-connect-registration-gate';
import { DirectConnectSynchronizeGate } from './direct-connect-synchronize-gate';
import { DirectConnectUserGate } from './direct-connect-user-gate';
import { DirectConnectWifiGate } from './direct-connect-wifi-gate';

export interface DirectConnectGateProps {
  serialNumber: string;
  children: ReactNode;
}

interface DirectConnectGateInternalProps {
  children: ReactNode;
}

const DirectConnectGate = (props: DirectConnectGateProps) => {
  const { children, serialNumber } = props;

  const isKioskEnabled = useIsKioskModeEnabled();

  if (isKioskEnabled) {
    return <DirectConnectKioskGate>{children}</DirectConnectKioskGate>;
  } else {
    return (
      <DirectConnectDefaultGate serialNumber={serialNumber}>
        {children}
      </DirectConnectDefaultGate>
    );
  }
};

const DirectConnectDefaultGate = (props: DirectConnectGateProps) => {
  const { children, serialNumber } = props;

  const wifiInfo = useMemo(() => {
    return {
      ssid: getBeaconWifiSsid(serialNumber),
      passphrase: serialNumber,
    };
  }, [serialNumber]);

  return (
    <DirectConnectGateKeeper>
      <DirectConnectWifiGate
        ssid={wifiInfo.ssid}
        passphrase={wifiInfo.passphrase}
      >
        <DirectConnectGateInternal>{children}</DirectConnectGateInternal>
      </DirectConnectWifiGate>
    </DirectConnectGateKeeper>
  );
};

const DirectConnectKioskGate = (
  props: Omit<DirectConnectGateProps, 'serialNumber'>
) => {
  const { children } = props;

  const contextValue = useMemo<DirectConnectNetworkContextValue>(() => {
    return {
      disconnectNetwork: async () => {
        return true;
      },
      hasError: false,
      networkInfo: {
        gateway: ApplicationConfig.robot.network.host,
        ipAddress: ApplicationConfig.robot.network.host,
      },
    };
  }, []);

  return (
    <DirectConnectGateKeeper>
      <DirectConnectNetworkContext.Provider value={contextValue}>
        <DirectConnectGateInternal>{children}</DirectConnectGateInternal>
      </DirectConnectNetworkContext.Provider>
    </DirectConnectGateKeeper>
  );
};

const DirectConnectGateInternal = (props: DirectConnectGateInternalProps) => {
  const { children } = props;

  const { networkInfo } = useDirectConnectNetworkContext();

  const endpoints = useMemo(() => {
    // always return localhost as fallback so we can continue to render children
    // properly in case of WiFi disconnect event
    const gateway = networkInfo?.gateway ?? '127.0.0.1';
    const base = getDirectConnectClientUrl(gateway);

    return {
      base,
      graphql: new URL('graphql', base).toString(),
    };
  }, [networkInfo?.gateway]);

  return (
    <DirectConnectHealthGate url={endpoints.base}>
      <UserProvider>
        <DirectConnectApiProvider apollo={{ uri: endpoints.graphql }}>
          <DirectConnectRegistrationGate>
            <DirectConnectDeactivateGate>
              <DirectConnectProvisioningGate>
                <DirectConnectSynchronizeGate>
                  <DirectConnectHealthGateMonitor>
                    <DirectConnectUserGate>
                      <IntercomSupportContextProvider>
                        {children}
                      </IntercomSupportContextProvider>
                    </DirectConnectUserGate>
                  </DirectConnectHealthGateMonitor>
                </DirectConnectSynchronizeGate>
              </DirectConnectProvisioningGate>
            </DirectConnectDeactivateGate>
          </DirectConnectRegistrationGate>
        </DirectConnectApiProvider>
      </UserProvider>
    </DirectConnectHealthGate>
  );
};

const DirectConnectHealthGateMonitor = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { hasFailedGate } = useDirectConnectGateKeeper();
  const { startHealthMonitor, stopHealthMonitor } =
    useDirectConnectHealthGateContext();

  useEffect(() => {
    if (hasFailedGate) {
      stopHealthMonitor();
    } else {
      startHealthMonitor();
    }
  }, [hasFailedGate, startHealthMonitor, stopHealthMonitor]);

  return <>{children}</>;
};

export { DirectConnectGate };
